import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Navigation, 
  Phone, 
  Clock, 
  MapPin, 
  RefreshCw,
  Car,
  Loader2,
  Radio,
  User,
  ArrowRight,
  X
} from 'lucide-react';
import { loadGoogleMaps } from '@/utils/loadGoogleMaps';
import { fitMapToMarkers, syncActiveServicesOnMap } from '@/utils/liveVehicleMapLayers';
import { updateDrivingRoutesOnMap } from '@/utils/drivingRoute';

function getClientBackendBaseUrl() {
  const env = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/+$/, '');
  if (env) return env;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return String(window.location.origin).replace(/\/+$/, '');
  }
  return '';
}

/** Normalize GPS point (driver/API may use lat/lng or latitude/longitude). */
function getGpsCoords(obj) {
  if (!obj || typeof obj !== 'object') return null;
  const lat = obj.lat ?? obj.latitude;
  const lng = obj.lng ?? obj.longitude;
  if (lat == null || lng == null) return null;
  const la = parseFloat(lat);
  const ln = parseFloat(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return null;
  return { lat: la, lng: ln, updated_at: obj.updated_at, speed: obj.speed, heading: obj.heading };
}

function getCurrentGps(service) {
  return getGpsCoords(service?.current_location);
}

function getPickupGps(service) {
  return getGpsCoords(service?.pickup_location);
}

function getDeliveryGps(service) {
  return getGpsCoords(service?.delivery_location);
}

// Default center (Spain)
const DEFAULT_CENTER = { lat: 40.4168, lng: -3.7038 };
const DEFAULT_ZOOM = 6;

const ClientGPSLiveTab = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('es') ? 'es-ES' : 'en-US';
  const [activeServices, setActiveServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [etaData, setEtaData] = useState({}); // {service_id: {duration: "45 min", distance: "62 km"}}
  
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const overlaysRef = useRef([]);
  const routeOverlaysRef = useRef([]);
  const routeFetchSeqRef = useRef(0);
  const infoWindowRef = useRef(null);
  const hasFittedMapRef = useRef(false);
  const servicesFingerprintRef = useRef('');

  // Initialize map when container is ready
  const initializeMap = useCallback((container) => {
    if (!container || mapInstanceRef.current) return;
    
    // Check if Google Maps is available
    if (!window.google || !window.google.maps) {
      return;
    }

    try {
      mapInstanceRef.current = new window.google.maps.Map(container, {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,  // Enable zoom controls (+/-)
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.RIGHT_CENTER
        },
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }]
          }
        ]
      });
      
      infoWindowRef.current = new window.google.maps.InfoWindow();
      
      setMapInitialized(true);
    } catch (error) {
      console.error('❌ Error initializing map:', error);
    }
  }, []);

  // Callback ref for map container
  const mapRef = useCallback((node) => {
    mapContainerRef.current = node;
    if (node && mapLoaded && !mapInstanceRef.current) {
      initializeMap(node);
    }
  }, [mapLoaded, initializeMap]);

  // Ensure Maps JS API is loaded (parent dashboard also calls this; repeat is safe)
  useEffect(() => {
    loadGoogleMaps().catch((err) => {
      console.warn('[ClientGPS] Google Maps failed to load:', err?.message || err);
    });
  }, []);

  // Load/check Google Maps Script
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (window.google && window.google.maps) {
        setMapLoaded(true);
        // Try to initialize if container is already mounted
        if (mapContainerRef.current && !mapInstanceRef.current) {
          initializeMap(mapContainerRef.current);
        }
        return true;
      }
      return false;
    };

    // If already loaded, set state immediately
    if (checkGoogleMaps()) return;

    // Otherwise, wait for it to load (App.js should load it)
    const intervalId = setInterval(() => {
      if (checkGoogleMaps()) {
        clearInterval(intervalId);
      }
    }, 100);

    // Cleanup
    return () => clearInterval(intervalId);
  }, [initializeMap]);

  const openLiveVehicleInfo = useCallback((service, position, marker) => {
    setSelectedService(service);
    const serviceEta = etaData[service.id];
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: position }, (results, status) => {
      if (status === 'OK' && results[0]) {
        setSelectedAddress(results[0].formatted_address);
        const infoContent = `
          <div style="padding: 10px; max-width: 300px; font-family: system-ui, -apple-system, sans-serif;">
            <div style="display:inline-block;background:#2563eb;color:#fff;font-size:10px;font-weight:700;padding:2px 8px;border-radius:4px;margin-bottom:6px;">
              ${t('gps_map_live_badge', { defaultValue: 'LIVE' })}
            </div>
            <div style="font-weight: bold; font-size: 16px; color: #1e40af; margin-bottom: 8px;">
              🚗 ${service.vehicle_info?.plate || t('client_na')}
            </div>
            <div style="color: #374151; font-size: 13px; margin-bottom: 4px;">
              <strong>${service.vehicle_info?.brand || ''} ${service.vehicle_info?.model || ''}</strong>
            </div>
            <div style="color: #6b7280; font-size: 12px; margin-bottom: 8px;">
              📍 <strong>${t('client_gps_infowindow_current_location')}</strong><br/>
              ${results[0].formatted_address}
            </div>
            ${service.delivery_location?.address ? `
              <div style="color: #6b7280; font-size: 12px; margin-bottom: 8px;">
                🏁 <strong>${t('client_gps_infowindow_destination')}</strong><br/>
                ${service.delivery_location.address}
              </div>
            ` : ''}
            ${serviceEta ? `
              <div style="background: #ecfdf5; border: 1px solid #10b981; border-radius: 8px; padding: 10px; margin: 10px 0;">
                <div style="color: #059669; font-size: 12px; font-weight: 600;">
                  ⏱️ ${t('client_gps_infowindow_eta_title')}
                </div>
                <div style="color: #047857; font-size: 22px; font-weight: bold; margin-top: 4px;">
                  ${serviceEta.duration}
                </div>
                <div style="color: #6b7280; font-size: 11px; margin-top: 2px;">
                  ${serviceEta.distance} ${t('client_gps_infowindow_eta_remaining')}
                </div>
              </div>
            ` : `
              <div style="background: #fef3c7; border: 1px solid #f59e0b; border-radius: 8px; padding: 8px; margin: 10px 0;">
                <div style="color: #b45309; font-size: 12px;">
                  ⏳ ${t('client_gps_infowindow_eta_calculating')}
                </div>
              </div>
            `}
            ${service.driver_info ? `
              <div style="color: #374151; font-size: 12px; border-top: 1px solid #e5e7eb; padding-top: 8px; margin-top: 8px;">
                <strong>${t('client_gps_infowindow_driver')}</strong> ${service.driver_info.name || t('client_na')}<br/>
                ${service.driver_info.phone ? `<a href="tel:${service.driver_info.phone}" style="color: #2563eb; text-decoration: none;">📞 ${service.driver_info.phone}</a>` : ''}
              </div>
            ` : ''}
          </div>
        `;
        infoWindowRef.current.setContent(infoContent);
        infoWindowRef.current.open(mapInstanceRef.current, marker);
      } else {
        setSelectedAddress(t('inspections_location_unavailable'));
      }
    });
  }, [etaData, t]);

  // Update markers + driving routes when services change
  useEffect(() => {
    if (!mapInstanceRef.current || !mapInitialized) return;

    const bounds = new window.google.maps.LatLngBounds();
    const { hasValidLocations, livePositions, skipped } = syncActiveServicesOnMap({
      map: mapInstanceRef.current,
      activeServices,
      markersRef,
      overlaysRef,
      bounds,
      getCurrentGps,
      getPickupGps,
      getDeliveryGps,
      getPlate: (s) => s.vehicle_info?.plate,
      t,
      onLiveVehicleClick: openLiveVehicleInfo,
    });

    if (hasValidLocations && !skipped && !hasFittedMapRef.current) {
      fitMapToMarkers(mapInstanceRef.current, bounds, { livePositions });
      hasFittedMapRef.current = true;
    }

    let cancelled = false;
    const seq = ++routeFetchSeqRef.current;
    updateDrivingRoutesOnMap({
      map: mapInstanceRef.current,
      activeServices,
      routeOverlaysRef,
      getCurrentGps,
      getPickupGps,
      getDeliveryGps,
    }).then((etas) => {
      if (cancelled || seq !== routeFetchSeqRef.current) return;
      const next = {};
      for (const [id, row] of Object.entries(etas)) {
        next[id] = {
          duration: row.durationText,
          durationValue: row.durationValue,
          distance: row.distanceText,
          distanceValue: row.distanceValue,
          calculatedAt: new Date(),
        };
      }
      setEtaData(next);
    });

    return () => {
      cancelled = true;
    };
  }, [activeServices, mapInitialized, openLiveVehicleInfo, t]);

  const fetchActiveServices = useCallback(async () => {
    const base = getClientBackendBaseUrl();
    if (!base) {
      setActiveServices([]);
      setLoading(false);
      setRefreshing(false);
      return;
    }
    try {
      const token = localStorage.getItem('session_token') || localStorage.getItem('token');
      const response = await fetch(`${base}/api/clients/transport-services/active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const next = data.services || [];
        const fp = next
          .map((s) => {
            const g = getCurrentGps(s);
            return `${s.id}:${g ? `${Number(g.lat).toFixed(3)},${Number(g.lng).toFixed(3)}` : '-'}:${s.status || ''}`;
          })
          .sort()
          .join('|');
        if (fp !== servicesFingerprintRef.current) {
          servicesFingerprintRef.current = fp;
          setActiveServices(next);
        }
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Error fetching active services:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchActiveServices();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchActiveServices();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchActiveServices]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchActiveServices();
  };

  const formatTime = (isoString) => {
    if (!isoString) return t('client_na');
    const date = new Date(isoString);
    return date.toLocaleTimeString(dateLocale, { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">{t('client_gps_loading_vehicles')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-gps-live-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Navigation className="w-6 h-6 text-blue-600" />
            {t('gps_live_title')}
          </h2>
          <p className="text-gray-600 mt-1">
            {t('client_gps_subtitle')}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {activeServices.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 rounded-full">
              <Radio className="w-4 h-4 text-green-600 animate-pulse" />
              <span className="text-sm font-medium text-green-700">
                {t('client_gps_on_route_count', { count: activeServices.length }).toUpperCase()}
              </span>
            </div>
          )}
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-gps-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {t('gps_live_refresh')}
          </Button>
        </div>
      </div>

      {/* Last Update Info */}
      {lastUpdate && (
        <p className="text-sm text-gray-500">
          {t('client_gps_last_update')} {lastUpdate.toLocaleTimeString(dateLocale)} 
          <span className="text-gray-400 ml-2">{t('client_gps_auto_refresh')}</span>
        </p>
      )}

      {/* Google Maps - Always visible */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            {t('client_gps_map_title')} 
            {activeServices.length > 0 && (
              <Badge className="ml-2 bg-blue-100 text-blue-700">
                {activeServices.length === 1
                  ? t('client_gps_vehicles_on_route', { count: activeServices.length })
                  : t('client_gps_vehicles_on_route_plural', { count: activeServices.length })}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Map Container - Always shown */}
          <div className="relative">
            <div 
              ref={mapRef}
              className="w-full h-[450px] rounded-lg border bg-gray-100"
              data-testid="gps-map-container"
              style={{ minHeight: '450px' }}
            />
            {!mapInitialized && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-600">{t('client_gps_loading_map')}</span>
              </div>
            )}
          </div>
          
          {/* Empty state message */}
          {activeServices.length === 0 && mapInitialized && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg text-center">
              <Car className="w-8 h-8 mx-auto text-gray-400 mb-2" />
              <p className="text-gray-600">{t('client_gps_empty_title')}</p>
              <p className="text-gray-400 text-sm mt-1">
                {t('client_gps_empty_hint')}
              </p>
            </div>
          )}

          {/* Legend */}
          <div className="mt-4 space-y-2 text-sm text-gray-600">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="flex items-center gap-1.5 shrink-0">
                <span className="w-3 h-3 rounded-full bg-green-500 inline-block" />
                {t('client_gps_route_origin')}
              </span>
              <span className="flex items-center gap-1.5 shrink-0">
                <span className="w-3 h-3 rounded-full bg-red-500 inline-block" />
                {t('client_gps_route_destination')}
              </span>
              <span className="flex items-center gap-1.5 shrink-0">
                <span className="relative flex h-4 w-4 items-center justify-center">
                  <span className="absolute h-4 w-4 rounded-full bg-green-400/40 animate-pulse" />
                  <Car className="relative w-3 h-3 text-blue-600" />
                </span>
                {t('gps_map_live_vehicle', { defaultValue: 'Live vehicle (highlighted)' })}
              </span>
              <span className="flex items-center gap-1.5 text-blue-700 shrink-0">
                <span className="w-6 border-t-2 border-dashed border-blue-600 inline-block" />
                {t('gps_map_route_to_destination', { defaultValue: 'Route to destination' })}
              </span>
            </div>
            <p className="text-gray-400 text-xs">{t('client_gps_legend_click')}</p>
          </div>
        </CardContent>
      </Card>

      {/* Vehicle Cards - Horizontal Scroll */}
      {activeServices.length > 0 && (
        <div className="overflow-x-auto pb-2">
          <div className="flex gap-4" style={{ minWidth: 'max-content' }}>
          {activeServices.map(service => (
            <Card 
              key={service.id} 
              className={`cursor-pointer transition-all hover:shadow-lg flex-shrink-0 w-[340px] ${selectedService?.id === service.id ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
              onClick={() => {
                setSelectedService(service);
                // Center map on this vehicle
                const cur = getCurrentGps(service);
                const fallback = getPickupGps(service) || getDeliveryGps(service);
                const target = cur || fallback;
                if (mapInstanceRef.current && target) {
                  mapInstanceRef.current.panTo({ lat: target.lat, lng: target.lng });
                  mapInstanceRef.current.setZoom(14);
                }
              }}
              data-testid={`vehicle-card-${service.vehicle_info?.plate}`}
            >
              <CardContent className="p-4">
                <div className="flex gap-4">
                  {/* Left: Vehicle Icon */}
                  <div className="flex-shrink-0 w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Car className="w-8 h-8 text-blue-600" />
                  </div>
                  
                  {/* Right: Info */}
                  <div className="flex-1 min-w-0">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold text-lg text-gray-900">{service.vehicle_info?.plate || t('client_na')}</span>
                      <Badge className="bg-green-100 text-green-700 text-xs">{t('client_gps_card_en_route')}</Badge>
                    </div>
                    
                    {/* Vehicle Model */}
                    <p className="text-gray-500 text-sm mb-2">
                      {service.vehicle_info?.brand} {service.vehicle_info?.model}
                    </p>
                    
                    {/* Route */}
                    <div className="flex items-center gap-1 text-xs text-gray-600 mb-2">
                      <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0"></div>
                      <span className="truncate">{service.pickup_location?.address?.split(',')[0] || t('client_gps_route_origin')}</span>
                      <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0 mx-1" />
                      <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0"></div>
                      <span className="truncate">{service.delivery_location?.address?.split(',')[0] || t('client_gps_route_destination')}</span>
                    </div>
                    
                    {/* Driver with phone inline */}
                    {service.driver_info && (
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <User className="w-3 h-3 text-gray-400" />
                        <span className="font-medium">{service.driver_info.name}</span>
                        {service.driver_info.phone && (
                          <>
                            <span className="text-gray-400">|</span>
                            <a 
                              href={`tel:${service.driver_info.phone}`}
                              className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Phone className="w-3 h-3" />
                              <span>{service.driver_info.phone}</span>
                            </a>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* ETA Bar - Full Width */}
                {etaData[service.id] ? (
                  <div className="flex items-center gap-2 mt-3 p-2 bg-green-50 rounded-lg border border-green-200">
                    <Clock className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-green-800 text-xs font-medium">{t('client_gps_eta_label')}</span>
                      <span className="text-green-700 font-bold ml-2">{etaData[service.id].duration}</span>
                      <span className="text-gray-500 text-xs ml-2">({etaData[service.id].distance})</span>
                    </div>
                  </div>
                ) : service.eta_minutes != null && Number.isFinite(Number(service.eta_minutes)) ? (
                  <div className="flex items-center gap-2 mt-3 p-2 bg-emerald-50 rounded-lg border border-emerald-200">
                    <Clock className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-emerald-800 text-xs font-medium">{t('client_gps_eta_label')}</span>
                      <span className="text-emerald-800 font-bold ml-2">
                        {t('client_gps_eta_backend_minutes', { minutes: Math.round(Number(service.eta_minutes)) })}
                      </span>
                    </div>
                  </div>
                ) : getDeliveryGps(service) && !getCurrentGps(service) ? (
                  <div className="flex items-center gap-2 mt-3 p-2 bg-amber-50 rounded-lg text-sm text-amber-800 border border-amber-100">
                    <Clock className="w-4 h-4 flex-shrink-0" />
                    <span>{t('client_gps_eta_waiting_gps')}</span>
                  </div>
                ) : getDeliveryGps(service) && getCurrentGps(service) ? (
                  <div className="flex items-center gap-2 mt-3 p-2 bg-gray-50 rounded-lg text-sm text-gray-400">
                    <Clock className="w-4 h-4" />
                    <span>{t('client_gps_eta_calculating')}</span>
                  </div>
                ) : null}

                {/* Last Update */}
                {getCurrentGps(service)?.updated_at && (
                  <p className="text-xs text-gray-400 mt-2">
                    {t('client_gps_last_position')} {formatTime(getCurrentGps(service).updated_at)}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
          </div>
        </div>
      )}

      {/* Selected Vehicle Detail Panel */}
      {selectedService && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Navigation className="w-5 h-5 text-blue-600" />
                {selectedService.vehicle_info?.plate} — {t('client_gps_detail_suffix')}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => {
                setSelectedService(null);
                setSelectedAddress(null);
                if (infoWindowRef.current) {
                  infoWindowRef.current.close();
                }
              }}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">{t('client_gps_label_vehicle')}</p>
                <p className="font-medium">{selectedService.vehicle_info?.brand} {selectedService.vehicle_info?.model}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('client_gps_label_driver')}</p>
                <p className="font-medium">{selectedService.driver_info?.name || t('client_na')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('client_gps_label_origin')}</p>
                <p className="font-medium text-sm">{selectedService.pickup_location?.address || t('client_na')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('client_gps_label_destination')}</p>
                <p className="font-medium text-sm">{selectedService.delivery_location?.address || t('client_na')}</p>
              </div>
              {selectedAddress && (
                <div className="col-span-2">
                  <p className="text-sm text-gray-500">{t('client_gps_label_current_location')}</p>
                  <p className="font-medium text-blue-700 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {selectedAddress}
                  </p>
                </div>
              )}
              {/* ETA in detail panel */}
              {etaData[selectedService.id] && (
                <div className="col-span-2 bg-green-100 rounded-lg p-3 border border-green-300">
                  <p className="text-sm text-green-700 font-medium flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {t('client_gps_infowindow_eta_title')}
                  </p>
                  <p className="text-2xl font-bold text-green-800 mt-1">
                    {etaData[selectedService.id].duration}
                  </p>
                  <p className="text-sm text-green-600">
                    {etaData[selectedService.id].distance} {t('client_gps_infowindow_eta_remaining')}
                  </p>
                </div>
              )}
              {!etaData[selectedService.id] &&
                selectedService.eta_minutes != null &&
                Number.isFinite(Number(selectedService.eta_minutes)) && (
                <div className="col-span-2 bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                  <p className="text-sm text-emerald-800 font-medium flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {t('client_gps_infowindow_eta_title')}
                  </p>
                  <p className="text-2xl font-bold text-emerald-900 mt-1">
                    {t('client_gps_eta_backend_minutes', { minutes: Math.round(Number(selectedService.eta_minutes)) })}
                  </p>
                </div>
              )}
              {selectedService.driver_info?.phone && (
                <div className="col-span-2">
                  <a 
                    href={`tel:${selectedService.driver_info.phone}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <Phone className="w-4 h-4" />
                    {t('client_gps_call_driver')}
                  </a>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientGPSLiveTab;
